import { ORDER_BY, TABLE_URL_PARAMS } from 'store/reportsManager/constants'

/**
 * Creates validators object for useValidatedTableParams hook
 * Centralizes validation logic and defaults for common table parameters
 *
 * @param {Object} config - Configuration object
 * @param {Array<string>} config.validSortKeys - Array of valid sort key strings (e.g., ['schools', 'status'])
 * @param {Array<string>} [config.validSiteIds] - Array of valid site IDs for site validation
 * @param {Array<string>} [config.validClassInstances] - Array of valid class instance IDs for class validation
 * @param {string} [config.defaultSortKey] - Default sort key (defaults to first key in validSortKeys)
 * @param {string} [config.defaultOrderBy=ORDER_BY.ASC] - Default order by value
 * @param {string} [config.defaultSiteId] - Default site ID (optional, only included if provided)
 * @param {Object} [config.urlParamKeyOverrides] - Optional override mapping for URL param names.
 *   Must match TABLE_URL_PARAMS structure (keys like SITE_ID, SORT_KEY, etc.).
 *   If not provided, defaults to TABLE_URL_PARAMS values internally.
 *   Example: { SITE_ID: 'siteId', SORT_KEY: 'sort' } overrides URL param names.
 *   IMPORTANT: managedParamKeys in useValidatedTableParams must use validator keys (not URL param names).
 * @returns {Object} Validators object with default, validate, normalize, and urlParamName functions for each param
 *
 * @example
 * const validators = createTableValidators({
 *   validSortKeys: Object.keys(TIER2_LESSON_COMPLETION_SORT_CONFIG),
 *   validSiteIds: ['123', '456'],
 *   defaultSortKey: 'smallGroups',
 *   defaultSiteId: '123'
 * })
 */
export const createTableValidators = ({
  validSortKeys = [],
  validSiteIds,
  validClassInstances,
  defaultSortKey,
  defaultOrderBy = ORDER_BY.ASC,
  defaultSiteId,
  urlParamKeyOverrides,
}) => {
  const sortKeyDefault = defaultSortKey || validSortKeys[0] || ''

  const validators = {
    sortKey: {
      default: sortKeyDefault,
      validate: value => validSortKeys.includes(value),
      normalize: value =>
        validSortKeys.includes(value) ? value : sortKeyDefault,
    },
    orderBy: {
      default: defaultOrderBy,
      validate: value => value === ORDER_BY.ASC || value === ORDER_BY.DESC,
      normalize: value =>
        [ORDER_BY.ASC, ORDER_BY.DESC].includes(value) ? value : defaultOrderBy,
    },
    currentPage: {
      default: '1',
      validate: value => {
        const num = parseInt(value, 10)
        return !isNaN(num) && num >= 1
      },
      normalize: value => {
        // Return as number (not string)
        const num = parseInt(value, 10)
        return isNaN(num) || num < 1 ? 1 : num
      },
    },
  }

  // Add siteId validator if defaultSiteId is provided
  if (defaultSiteId !== undefined) {
    validators.siteId = {
      default: defaultSiteId,
      validate: value =>
        !validSiteIds ||
        validSiteIds.length === 0 ||
        validSiteIds.includes(value),
      normalize: value => {
        if (!validSiteIds || validSiteIds.length === 0) {
          // Convert to number if it's a string
          if (value) {
            const parsed = parseInt(value, 10)
            return isNaN(parsed) ? null : parsed
          }
          return null
        }
        const validatedValue = validSiteIds.includes(value)
          ? value
          : validSiteIds[0]
        // Convert to number
        if (validatedValue) {
          const parsed = parseInt(validatedValue, 10)
          return isNaN(parsed) ? null : parsed
        }
        return null
      },
    }
  }

  // Add classInstance validator if validClassInstances is provided
  // NOTE: Invalid values are normalized to null (removes param from URL).
  // This handles:
  // - Bookmarked URLs with stale class IDs (class no longer in validClassInstances)
  // - Malicious URL manipulation (invalid class IDs)
  // Valid classInstance params that cause API errors will still show errors via error component.
  if (
    validClassInstances !== undefined &&
    validClassInstances !== null &&
    Array.isArray(validClassInstances) &&
    validClassInstances.length > 0
  ) {
    validators.classInstance = {
      // No default - invalid values are removed (normalized to null)
      validate: value => {
        // Empty/null values are valid (drawer closed state)
        if (!value) return true
        // Check if value is in validClassInstances array
        return validClassInstances.includes(value)
      },
      normalize: (value, defaultValue) => {
        // defaultValue parameter is unused (no default for classInstance)
        // but included for consistency with other validators
        // Empty/null values are valid (drawer closed state) - return as-is
        if (!value) return value
        // If valid, return as-is; if invalid, return null to remove param from URL
        if (validClassInstances.includes(value)) {
          return value
        }
        // Invalid value - return null (removes param from URL)
        // This handles stale bookmarks and malicious URL manipulation
        return null
      },
    }
  }

  // Map TABLE_URL_PARAMS keys to validator keys
  // This tells us which urlParamKeyOverrides key (SITE_ID) maps to which validator key (siteId)
  // Derived from TABLE_URL_PARAMS structure - keys (SITE_ID) map to validator keys (siteId)
  const tableUrlParamToValidatorKey = {
    SITE_ID: 'siteId',
    SORT_KEY: 'sortKey',
    ORDER_BY: 'orderBy',
    CURRENT_PAGE: 'currentPage',
    PAGE_SIZE: 'pageSize',
    CLASS_INSTANCE: 'classInstance',
  }

  // Create mapping from validator keys to URL param names
  // urlParamKeyOverrides has shape { SITE_ID: 'customSite', SORT_KEY: 'sort', ... } (matches TABLE_URL_PARAMS structure)
  // We transform it to { siteId: 'customSite', sortKey: 'sort', ... } for use with validator keys
  // Defaults to TABLE_URL_PARAMS if urlParamKeyOverrides not provided
  const finalUrlParamNames = urlParamKeyOverrides ?? TABLE_URL_PARAMS
  const validatorKeyToUrlParamName = {}
  Object.entries(tableUrlParamToValidatorKey).forEach(
    ([paramKey, validatorKey]) => {
      // Use custom value if provided, otherwise use TABLE_URL_PARAMS value, fallback to validator key
      validatorKeyToUrlParamName[validatorKey] =
        finalUrlParamNames[paramKey] ??
        TABLE_URL_PARAMS[paramKey] ??
        validatorKey
    },
  )

  // Store urlParamName in each validator
  // If a validator key doesn't have a mapping, fallback to the validator key name itself
  Object.keys(validators).forEach(key => {
    validators[key].urlParamName = validatorKeyToUrlParamName[key] ?? key
  })

  return validators
}

/**
 * Injects Redux values as preferredDefaults into validators
 * When URL is empty, hook will use preferredDefault (if valid) instead of default
 *
 * This allows Redux state to be used as initial values when the URL doesn't have params.
 * The preferredDefault is validated before use - if invalid, the hardcoded default is used.
 *
 * @param {Object} validators - Validators object from createTableValidators
 * @param {Object} reduxValues - Object mapping param keys to Redux values
 *   Example: { sortKey: 'status', orderBy: 'desc', currentPage: 2 }
 * @returns {Object} Validators object with preferredDefault injected for matching keys
 *
 * @example
 * // Basic usage:
 * const baseValidators = createTableValidators({
 *   validSortKeys: ['schools', 'status'],
 *   defaultSortKey: 'schools',
 * })
 *
 * const reduxSortKey = useSelector(state => state.reportsManager.sortKey)
 * const validators = injectReduxValues(baseValidators, {
 *   sortKey: reduxSortKey, // Will be used if URL is empty and value is valid
 * })
 *
 * @example
 * // Full Redux sync cycle with useValidatedTableParams:
 * const dispatch = useDispatch()
 * const reduxSortKey = useSelector(state => state.reportsManager.sortKey)
 * const reduxOrderBy = useSelector(state => state.reportsManager.orderBy)
 *
 * // 1. Create base validators
 * const baseValidators = useMemo(() => createTableValidators({
 *   validSortKeys: Object.keys(SORT_CONFIG),
 *   defaultSortKey: 'schools',
 * }), [])
 *
 * // 2. Inject Redux values as preferred defaults
 * const validators = useMemo(() => injectReduxValues(baseValidators, {
 *   sortKey: reduxSortKey,
 *   orderBy: reduxOrderBy,
 * }), [baseValidators, reduxSortKey, reduxOrderBy])
 *
 * // 3. Use hook with validators
 * const { validated, updateUrlParams } = useValidatedTableParams({
 *   validators,
 *   managedParamKeys: ['sortKey', 'orderBy'],
 *   onValidatedParamsChange: useCallback((validated) => {
 *     // 4. Sync validated values back to Redux
 *     dispatch(updateSortKey(validated.sortKey))
 *     dispatch(updateOrderBy(validated.orderBy))
 *   }, [dispatch])
 * })
 *
 * // Cycle:
 * // - If URL has params → use URL (validated), sync to Redux
 * // - If URL empty + Redux has valid values → use Redux (validated), update URL, sync to Redux
 * // - If URL empty + Redux invalid/missing → use defaults, update URL, sync to Redux
 */
export const injectReduxValues = (validators, reduxValues) => {
  return Object.keys(validators).reduce((acc, key) => {
    acc[key] = {
      ...validators[key],
      // Only inject if reduxValues has a value for this key (undefined values are skipped)
      ...(reduxValues[key] !== undefined && {
        preferredDefault: reduxValues[key],
      }),
    }
    return acc
  }, {})
}
